/**
 * ChannelDeploymentManager.js
 *
 * Deploy chatbot workflows to messaging channels.
 * Each channel type has a tailored config form, live status,
 * webhook URL display, and deploy/undeploy controls.
 *
 * Usage:
 *   <ChannelDeploymentManager
 *     workflowId={workflowId}
 *     workflowName="My Bot"
 *     isOpen={channelsOpen}
 *     onClose={() => setChannelsOpen(false)}
 *   />
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Radio, Check, AlertCircle, RefreshCw, Copy, ExternalLink,
  ChevronRight, ChevronDown, Trash2, Power, PowerOff, Globe, Shield,
  Send, MessageSquare, Hash, Key, Link2, Eye, EyeOff, HelpCircle, Zap
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

// ==================== Channel Definitions ====================

const authHeaders = () => {
  const token = getCurrentToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const CHANNELS = [
  {
    type: 'web',
    label: 'Web Widget',
    icon: '🌐',
    color: 'bg-sky-500',
    desc: 'Embeddable chat widget for your website. Uses SSE/polling — no webhook needed.',
    configFields: [
      { key: 'allowed_origins', label: 'Allowed Origins', placeholder: 'https://yoursite.com, https://app.yoursite.com', help: 'Comma-separated domains allowed to embed the widget. Leave blank to allow all.', type: 'text' },
      { key: 'welcome_message', label: 'Welcome Message', placeholder: 'Hi! How can I help you today?', help: 'Initial message shown when widget opens.', type: 'text' },
      { key: 'theme_color', label: 'Theme Color', placeholder: '#3B82F6', help: 'Primary color for the chat widget.', type: 'color' },
    ],
    needsWebhook: false,
  },
  {
    type: 'whatsapp',
    label: 'WhatsApp',
    icon: '💬',
    color: 'bg-green-500',
    desc: 'WhatsApp Business API via Cloud API. Requires a Meta Business account.',
    configFields: [
      { key: 'phone_id', label: 'Phone Number ID', placeholder: '1234567890', help: 'From Meta Developer Console > WhatsApp > Getting Started.', type: 'text', required: true },
      { key: 'token', label: 'Access Token', placeholder: 'EAABs...', help: 'Permanent token from System Users in Meta Business Settings.', type: 'secret', required: true },
      { key: 'verify_token', label: 'Verify Token', placeholder: 'my_verify_token', help: 'Any string you choose. Must match webhook config in Meta Console.', type: 'text', required: true },
      { key: 'app_secret', label: 'App Secret', placeholder: 'abc123...', help: 'From Meta Developer Console > App Settings > Basic. Used to verify webhook signatures.', type: 'secret', required: true },
    ],
    needsWebhook: true,
    webhookHelp: 'In Meta Developer Console, set the webhook URL and subscribe to "messages" events.',
  },
  {
    type: 'whatsapp_flow',
    label: 'WhatsApp Flows',
    icon: '📋',
    color: 'bg-green-600',
    desc: 'Interactive WhatsApp Flows with structured forms and screens.',
    configFields: [
      { key: 'phone_id', label: 'Phone Number ID', placeholder: '1234567890', type: 'text', required: true },
      { key: 'token', label: 'Access Token', placeholder: 'EAABs...', type: 'secret', required: true },
      { key: 'verify_token', label: 'Verify Token', placeholder: 'my_verify_token', type: 'text', required: true },
      { key: 'app_secret', label: 'App Secret', placeholder: 'abc123...', type: 'secret', required: true },
      { key: 'flow_id', label: 'WhatsApp Flow ID', placeholder: '987654321', help: 'The Flow ID from WhatsApp Manager.', type: 'text', required: true },
      { key: 'private_key', label: 'Flow Private Key (PEM)', placeholder: '-----BEGIN PRIVATE KEY-----\n...', help: 'RSA private key for data exchange endpoint encryption.', type: 'textarea', required: true },
    ],
    needsWebhook: true,
    webhookHelp: 'Configure both the standard webhook URL and the data exchange endpoint URL in WhatsApp Manager.',
  },
  {
    type: 'whatsapp_interakt',
    label: 'WhatsApp (Interakt)',
    icon: '💬',
    color: 'bg-emerald-500',
    desc: 'WhatsApp Business API via Interakt BSP. Full Cloud API proxy with templates, flows, commerce, and analytics.',
    configFields: [
      { key: 'access_token', label: 'Access Token', placeholder: 'your_interakt_access_token', help: 'x-access-token from Interakt Partner Portal. Found in API Settings.', type: 'secret', required: true },
      { key: 'waba_id', label: 'WABA ID', placeholder: '123456789012345', help: 'WhatsApp Business Account ID from Meta Business Manager.', type: 'text', required: true },
      { key: 'phone_number_id', label: 'Phone Number ID', placeholder: '987654321012345', help: 'Phone Number ID from Meta Developer Console or Interakt.', type: 'text', required: true },
      { key: 'app_secret', label: 'App Secret (optional)', placeholder: 'abc123...', help: 'For webhook signature verification (X-Hub-Signature-256). Recommended for production.', type: 'secret' },
      { key: 'verify_token', label: 'Verify Token', placeholder: 'my_verify_token', help: 'Any string for webhook verification handshake.', type: 'text', required: true, generateButton: true },
    ],
    needsWebhook: true,
    webhookHelp: 'In Interakt Partner Portal → Webhook Settings, set the callback URL and verify token. Subscribe to message events.',
    setupSteps: [
      'Get your access token and WABA ID from Interakt Partner Portal',
      'Find your Phone Number ID in Meta Business Manager or Interakt console',
      'Enter credentials above and generate a verify token',
      'Click Deploy, then configure the webhook URL in Interakt Portal',
    ],
  },
  {
    type: 'jbm_rcs',
    label: 'JBM RCS',
    icon: '📱',
    color: 'bg-blue-700',
    desc: 'Jio Business Messaging (RCS) — rich cards, carousels, suggestion chips. Reaches Jio users natively.',
    configFields: [
      { key: 'assistant_id', label: 'Assistant (Bot) ID', placeholder: 'your_bot_id', help: 'From JBM Console → Bot Settings. This is the client_id for OAuth.', type: 'text', required: true },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'your_client_secret', help: 'From JBM Console → Bot Settings. Used for OAuth token exchange.', type: 'secret', required: true },
      { key: 'environment', label: 'Environment', placeholder: 'prod', help: '"prod" or "staging". Use staging for testing before going live.', type: 'text' },
    ],
    needsWebhook: true,
    webhookHelp: 'In JBM Console → Webhook Configuration, set the callback URL shown below. JBM sends events for messages, read receipts, and delivery status.',
    setupSteps: [
      'Create a bot in JBM Console (console.businessmessaging.jio.com)',
      'Copy the Assistant ID and Client Secret from Bot Settings',
      'Select environment (use "staging" for testing)',
      'Click Deploy, then configure the webhook URL in JBM Console',
    ],
  },
  {
    type: 'telegram',
    label: 'Telegram',
    icon: '✈️',
    color: 'bg-blue-500',
    desc: 'Telegram Bot via BotFather. Simple setup with token and webhook.',
    configFields: [
      { key: 'token', label: 'Bot Token', placeholder: '123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11', help: 'Get this from @BotFather on Telegram using /newbot command.', type: 'secret', required: true },
      { key: 'webhook_secret', label: 'Webhook Secret', placeholder: 'my-secret-string', help: 'Any random string. Telegram sends this in X-Telegram-Bot-Api-Secret-Token header for verification.', type: 'text', required: true, generateButton: true },
    ],
    needsWebhook: true,
    webhookHelp: 'After deploying, call the Telegram setWebhook API with the webhook URL and secret_token shown below.',
    setupSteps: [
      'Message @BotFather on Telegram and use /newbot to create a bot',
      'Copy the bot token into the field above',
      'Generate or enter a webhook secret',
      'Click Deploy, then run the setWebhook command shown',
    ],
  },
  {
    type: 'instagram',
    label: 'Instagram DM',
    icon: '📸',
    color: 'bg-gradient-to-br from-purple-500 to-pink-500',
    desc: 'Instagram Direct Messages via Meta Graph API.',
    configFields: [
      { key: 'page_id', label: 'Facebook Page ID', placeholder: '1234567890', help: 'The Facebook Page connected to your Instagram Professional account.', type: 'text', required: true },
      { key: 'page_access_token', label: 'Page Access Token', placeholder: 'EAABs...', help: 'Long-lived token with instagram_manage_messages permission.', type: 'secret', required: true },
      { key: 'instagram_account_id', label: 'Instagram Account ID', placeholder: '17841400000', help: 'Your Instagram Professional Account ID (from Graph API).', type: 'text', required: true },
      { key: 'app_secret', label: 'App Secret', placeholder: 'abc123...', help: 'For webhook signature verification.', type: 'secret', required: true },
    ],
    needsWebhook: true,
    webhookHelp: 'In Meta Developer Console, add the webhook URL and subscribe to "messages" field under Instagram.',
  },
  {
    type: 'facebook',
    label: 'Messenger',
    icon: '💬',
    color: 'bg-blue-600',
    desc: 'Facebook Messenger via Meta Send API.',
    configFields: [
      { key: 'page_id', label: 'Facebook Page ID', placeholder: '1234567890', type: 'text', required: true },
      { key: 'page_access_token', label: 'Page Access Token', placeholder: 'EAABs...', help: 'Long-lived token with pages_messaging permission.', type: 'secret', required: true },
      { key: 'app_secret', label: 'App Secret', placeholder: 'abc123...', help: 'For webhook signature verification.', type: 'secret', required: true },
    ],
    needsWebhook: true,
    webhookHelp: 'In Meta Developer Console, set the webhook URL and subscribe to "messages" and "messaging_postbacks".',
  },
  {
    type: 'api',
    label: 'Custom API',
    icon: '🔌',
    color: 'bg-gray-700',
    desc: 'REST API endpoint for custom integrations. Use your workflow API key to send messages programmatically.',
    configFields: [
      { key: 'rate_limit', label: 'Rate Limit (req/min)', placeholder: '100', help: 'Max requests per minute per user.', type: 'number' },
    ],
    needsWebhook: false,
  },
];

// ==================== Helpers ====================

const generateSecret = (len = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(len))).map(b => chars[b % chars.length]).join('');
};

const getWebhookUrl = (workflowId, channelType) => {
  const base = window.location.origin;
  return `${base}/api/chatbot/webhook/${workflowId}/${channelType}`;
};

const getTelegramSetWebhookCmd = (botToken, webhookUrl, secret) => {
  return `curl -X POST "https://api.telegram.org/bot${botToken || '{BOT_TOKEN}'}/setWebhook" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "${webhookUrl}", "secret_token": "${secret || '{SECRET}'}"}'`;
};

// ==================== Copyable Field ====================

const CopyField = ({ label, value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-2">
      {label && <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</label>}
      <div className="flex items-center gap-1.5 mt-1">
        <code className={`flex-1 px-3 py-1.5 bg-gray-900 text-green-400 rounded-lg text-xs ${mono ? 'font-mono' : ''} break-all select-all`}>
          {value}
        </code>
        <button onClick={copy} className="p-1.5 bg-gray-900 text-gray-400 hover:text-white rounded-lg transition-colors flex-shrink-0" title="Copy">
          {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
};

// ==================== Config Field Renderer ====================

const ConfigField = ({ field, value, onChange }) => {
  const [showSecret, setShowSecret] = useState(false);

  if (field.type === 'textarea') {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-400">*</span>}
        </label>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono resize-none"
        />
        {field.help && <p className="mt-1 text-[11px] text-gray-400">{field.help}</p>}
      </div>
    );
  }

  if (field.type === 'secret') {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-400">*</span>}
        </label>
        <div className="relative">
          <input
            type={showSecret ? 'text' : 'password'}
            value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
          <button
            type="button"
            onClick={() => setShowSecret(!showSecret)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
          >
            {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        {field.help && <p className="mt-1 text-[11px] text-gray-400">{field.help}</p>}
      </div>
    );
  }

  if (field.type === 'color') {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value || '#3B82F6'}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
        </div>
        {field.help && <p className="mt-1 text-[11px] text-gray-400">{field.help}</p>}
      </div>
    );
  }

  // Default: text, number
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label} {field.required && <span className="text-red-400">*</span>}
      </label>
      <div className="flex items-center gap-2">
        <input
          type={field.type || 'text'}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {field.generateButton && (
          <button
            onClick={() => onChange(field.key, generateSecret())}
            className="px-2.5 py-2 text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
            title="Generate random value"
          >
            <Zap className="h-3.5 w-3.5 inline mr-1" />
            Generate
          </button>
        )}
      </div>
      {field.help && <p className="mt-1 text-[11px] text-gray-400">{field.help}</p>}
    </div>
  );
};

// ==================== Channel Card ====================

const ChannelCard = ({ channel, workflowId, deployed, onDeploy, onUndeploy, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [config, setConfig] = useState({});
  const [deploying, setDeploying] = useState(false);
  const [undeploying, setUndeploying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [secretFields, setSecretFields] = useState([]);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Load config from server when card is expanded for a deployed channel
  useEffect(() => {
    if (expanded && deployed && !configLoaded) {
      const loadConfig = async () => {
        setConfigLoading(true);
        try {
          const res = await apiCall(
            `/api/chatbot/workflows/${workflowId}/channels/${channel.type}`,
            { headers: authHeaders() }
          );
          if (res.ok) {
            const data = await res.json();
            setConfig(data.config || {});
            setSecretFields(data.secret_fields || []);
            setConfigLoaded(true);
          }
        } catch (err) {
          console.error('Failed to load channel config:', err);
        }
        setConfigLoading(false);
      };
      loadConfig();
    }
  }, [expanded, deployed, configLoaded, workflowId, channel.type]);

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setError('');
  };

  const handleDeploy = async () => {
    // Validate required fields — skip secret fields that still have masked values
    // (they're already saved server-side)
    const missing = channel.configFields
      .filter(f => {
        if (!f.required) return false;
        const val = config[f.key];
        if (!val || !val.toString().trim()) return true;
        // Masked secrets are valid (unchanged from server)
        if (secretFields.includes(f.key) && val.includes('••••')) return false;
        return false;
      })
      .map(f => f.label);
    if (missing.length > 0) {
      setError(`Required: ${missing.join(', ')}`);
      return;
    }

    setDeploying(true);
    setError('');
    try {
      const webhookUrl = channel.needsWebhook ? getWebhookUrl(workflowId, channel.type) : undefined;

      // Build config: strip masked secret values so server keeps existing ones
      const cleanConfig = {};
      for (const [key, value] of Object.entries(config)) {
        if (secretFields.includes(key) && value && value.includes('••••')) {
          // Don't send masked value — server keeps the existing secret
          continue;
        }
        cleanConfig[key] = value;
      }

      const body = {
        workflow_id: workflowId,
        channel_type: channel.type,
        channel_name: channel.type,
        config: { ...cleanConfig, ...(webhookUrl ? { webhook_url: webhookUrl } : {}) },
        is_active: true,
      };

      const res = await apiCall(`/api/chatbot/workflows/${workflowId}/channels`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Deploy failed (${res.status})`);
      }

      setSuccess('Deployed successfully');
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
    setDeploying(false);
  };

  const handleUndeploy = async () => {
    setUndeploying(true);
    setError('');
    try {
      // Deploy with is_active = false
      const body = {
        workflow_id: workflowId,
        channel_type: channel.type,
        channel_name: channel.type,
        config: config,
        is_active: false,
      };
      const res = await apiCall(`/api/chatbot/workflows/${workflowId}/channels`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Undeploy failed');

      setSuccess('Channel deactivated');
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
    setUndeploying(false);
  };

  const isActive = deployed?.is_active;
  const webhookUrl = channel.needsWebhook ? getWebhookUrl(workflowId, channel.type) : null;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? 'border-gray-300 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={`w-9 h-9 ${channel.color} rounded-lg flex items-center justify-center text-lg flex-shrink-0`}>
          {channel.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{channel.label}</span>
            {deployed && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                {isActive ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">{channel.desc}</p>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>

      {/* Expanded config */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/50">
          {/* Loading config from server */}
          {configLoading && (
            <div className="flex items-center justify-center py-6 gap-2">
              <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
              <span className="text-xs text-gray-500">Loading configuration...</span>
            </div>
          )}

          {!configLoading && (
          <>
          {/* Setup steps (if defined) */}
          {channel.setupSteps && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-xs font-semibold text-blue-700">Setup Steps</span>
              </div>
              <ol className="space-y-1.5">
                {channel.setupSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-blue-800">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center text-[10px] font-bold mt-0.5">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Config fields */}
          <div className="space-y-3">
            {channel.configFields.map(field => (
              <ConfigField
                key={field.key}
                field={field}
                value={config[field.key]}
                onChange={updateConfig}
              />
            ))}
          </div>

          {/* Webhook URL */}
          {webhookUrl && (
            <div className="mt-4">
              <CopyField label="Webhook URL" value={webhookUrl} />
            </div>
          )}

          {/* Telegram-specific: setWebhook command */}
          {channel.type === 'telegram' && config.token && (
            <div className="mt-3">
              <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Set Webhook Command</label>
              <div className="mt-1 relative">
                <pre className="px-3 py-2 bg-gray-900 text-green-400 rounded-lg text-[11px] font-mono whitespace-pre-wrap break-all overflow-x-auto">
                  {getTelegramSetWebhookCmd(config.token, webhookUrl, config.webhook_secret)}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(getTelegramSetWebhookCmd(config.token, webhookUrl, config.webhook_secret));
                  }}
                  className="absolute top-2 right-2 p-1 bg-gray-800 text-gray-400 hover:text-white rounded transition-colors"
                  title="Copy command"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* Webhook help */}
          {channel.webhookHelp && (
            <p className="mt-3 text-[11px] text-gray-400 flex items-start gap-1.5">
              <HelpCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
              {channel.webhookHelp}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            {!deployed || !isActive ? (
              <button
                onClick={handleDeploy}
                disabled={deploying}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deploying ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
                {deploying ? 'Deploying...' : deployed ? 'Re-activate' : 'Deploy'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleDeploy}
                  disabled={deploying}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deploying ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {deploying ? 'Updating...' : 'Update Config'}
                </button>
                <button
                  onClick={handleUndeploy}
                  disabled={undeploying}
                  className="flex items-center gap-1.5 px-3 py-2 text-red-600 bg-red-50 border border-red-200 text-sm font-medium rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                >
                  {undeploying ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
                  Deactivate
                </button>
              </>
            )}

            {deployed && (
              <span className="ml-auto text-[11px] text-gray-400">
                Deployed {new Date(deployed.created_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Error / Success */}
          {error && (
            <div className="mt-3 flex items-center gap-2 text-red-600 text-xs bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
            </div>
          )}
          {success && (
            <div className="mt-3 flex items-center gap-2 text-green-600 text-xs bg-green-50 rounded-lg px-3 py-2">
              <Check className="h-3.5 w-3.5 flex-shrink-0" /> {success}
            </div>
          )}
          </>)}
        </div>
      )}
    </div>
  );
};

// ==================== Main Component ====================

const ChannelDeploymentManager = ({ workflowId, workflowName, isOpen, onClose }) => {
  const [deployedChannels, setDeployedChannels] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadChannels = useCallback(async () => {
    if (!workflowId || !isOpen) return;
    setLoading(true);
    try {
      const res = await apiCall(`/api/chatbot/workflows/${workflowId}/channels`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDeployedChannels(data.channels || []);
      }
    } catch (err) {
      console.error('Failed to load channels:', err);
    }
    setLoading(false);
  }, [workflowId, isOpen]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  if (!isOpen) return null;

  const getDeployed = (type) => deployedChannels.find(c => c.channel_type === type);
  const activeCount = deployedChannels.filter(c => c.is_active).length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <Radio className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Deploy to Channels</h2>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-80">
                  <span className="font-medium text-gray-700">{workflowName || workflowId}</span>
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Active count */}
          <div className="flex items-center gap-2 mt-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              activeCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`w-2 h-2 rounded-full ${activeCount > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              {activeCount} active channel{activeCount !== 1 ? 's' : ''}
            </span>
            <button onClick={loadChannels} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors" title="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Channel list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && deployedChannels.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              {CHANNELS.map(channel => (
                <ChannelCard
                  key={channel.type}
                  channel={channel}
                  workflowId={workflowId}
                  deployed={getDeployed(channel.type)}
                  onDeploy={() => {}}
                  onUndeploy={() => {}}
                  onRefresh={loadChannels}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Channel credentials are stored encrypted server-side and never exposed via API. Webhook URLs require proper signature verification.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChannelDeploymentManager;
